import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/services/api';
import { useTheme } from '@/hooks/useTheme';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';

interface HourlyRow {
  hour: number;
  market: 'DAM' | 'IDM';
  price: number;
  volume: number;
}
interface HourlyResp { date: string; rows: HourlyRow[] }

const fmt = (v: any, d = 0): string => {
  if (v == null || isNaN(Number(v))) return '—';
  return Number(v).toLocaleString('uk-UA', { minimumFractionDigits: d, maximumFractionDigits: d });
};

const today = () => new Date().toISOString().slice(0, 10);
const dayOffset = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const KpiCell = ({ label, value, unit, color, isDark }: any) => (
  <div className="rounded-lg p-3" style={{ background: isDark ? 'rgba(255,255,255,0.03)' : '#F9F9FB' }}>
    <div className="text-[10px] uppercase tracking-wide" style={{ color: isDark ? '#6B6B70' : '#6B7280' }}>{label}</div>
    <div className="text-xl font-semibold mt-1" style={{ color }}>{value}</div>
    <div className="text-[10px]" style={{ color: isDark ? '#6B6B70' : '#6B7280' }}>{unit}</div>
  </div>
);

export default function OreeHourlyWidget() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [date, setDate] = useState<string>(today());
  const [data, setData] = useState<HourlyResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(null);
    api.get<HourlyResp>(`/trading/oree/hourly?date=${date}`)
      .then((r) => { if (!cancelled) setData(r); })
      .catch((e) => { if (!cancelled) setErr(e?.message || 'fetch failed'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [date]);

  // Auto-fallback to yesterday if DAM has no rows for selected date
  useEffect(() => {
    if (!loading && data && data.rows.filter(r => r.market === 'DAM').length === 0 && date === today()) {
      setDate(dayOffset(-1));
    }
  }, [data, loading, date]);

  const damRows = (data?.rows || []).filter(r => r.market === 'DAM').sort((a, b) => a.hour - b.hour);

  const chartData = useMemo(() => damRows.map(r => ({
    hour: `${r.hour}:00`,
    price: r.price,
    volume: r.volume,
  })), [damRows]);

  // KPIs
  const stats = useMemo(() => {
    if (!damRows.length) return null;
    const prices = damRows.map(r => r.price);
    const volumes = damRows.map(r => r.volume);
    const totalVol = volumes.reduce((a, b) => a + b, 0);
    const wAvg = damRows.reduce((s, r) => s + r.price * r.volume, 0) / (totalVol || 1);
    const maxIdx = prices.indexOf(Math.max(...prices));
    const minIdx = prices.indexOf(Math.min(...prices));
    return {
      wAvg,
      peakPrice: prices[maxIdx], peakHour: damRows[maxIdx].hour,
      lowPrice:  prices[minIdx], lowHour:  damRows[minIdx].hour,
      totalVol,
    };
  }, [damRows]);

  const cardBg = isDark ? '#1A1A1F' : '#FFFFFF';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';
  const chartGrid = isDark ? 'rgba(255,255,255,0.06)' : '#F3F4F6';
  const chartTick = isDark ? '#6B6B70' : '#6B7280';

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
      <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${cardBorder}` }}>
        <div className="flex items-center gap-2">
          <i className="ri-line-chart-line text-base" style={{ color: '#F97316' }}></i>
          <div>
            <h3 className="font-semibold text-sm" style={{ color: textMain }}>{t('oree_hourly_widget_title')}</h3>
            <div className="text-[10px]" style={{ color: textMuted }}>{t('oree_hourly_widget_subtitle')}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date" value={date} max={today()}
            onChange={e => setDate(e.target.value)}
            className="text-xs px-2 py-1 rounded cursor-pointer"
            style={{ background: 'transparent', border: `1px solid ${cardBorder}`, color: textMain }}
          />
          <button
            type="button" onClick={() => navigate('/trading/dam')}
            className="text-xs hover:underline cursor-pointer whitespace-nowrap"
            style={{ color: '#F97316' }}
          >
            {t('ueex_widget_view_all')} →
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 px-4 pt-4">
        <KpiCell
          label={t('oree_hourly_kpi_wavg')}
          value={fmt(stats?.wAvg, 0)}
          unit="грн/МВт·год"
          color="#F97316" isDark={isDark}
        />
        <KpiCell
          label={t('oree_hourly_kpi_peak')}
          value={fmt(stats?.peakPrice, 0)}
          unit={stats ? `о ${stats.peakHour}:00` : '—'}
          color="#EF4444" isDark={isDark}
        />
        <KpiCell
          label={t('oree_hourly_kpi_low')}
          value={fmt(stats?.lowPrice, 0)}
          unit={stats ? `о ${stats.lowHour}:00` : '—'}
          color="#10B981" isDark={isDark}
        />
        <KpiCell
          label={t('oree_hourly_kpi_total_vol')}
          value={fmt(stats?.totalVol, 0)}
          unit="МВт·год"
          color="#3B82F6" isDark={isDark}
        />
      </div>

      {/* Chart */}
      <div className="px-4 pt-3 pb-2">
        {loading && (
          <div className="text-xs py-8 text-center" style={{ color: textMuted }}>
            {t('common_loading') || 'Завантаження…'}
          </div>
        )}
        {err && !loading && (
          <div className="text-xs py-8 text-center text-red-500">{err}</div>
        )}
        {!loading && !err && damRows.length === 0 && (
          <div className="py-12 text-center text-sm" style={{ color: textMuted }}>
            <i className="ri-time-line text-2xl block mb-2"></i>
            {t('oree_hourly_empty')}
          </div>
        )}
        {!loading && !err && damRows.length > 0 && (
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid stroke={chartGrid} strokeDasharray="3 3" />
              <XAxis dataKey="hour" tick={{ fill: chartTick, fontSize: 10 }} />
              <YAxis yAxisId="left" tick={{ fill: chartTick, fontSize: 10 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fill: chartTick, fontSize: 10 }} />
              <Tooltip contentStyle={{ background: cardBg, border: `1px solid ${cardBorder}`, fontSize: 11, color: textMain }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar yAxisId="right" dataKey="volume" name="Обсяг, МВт·год" fill="#F97316" opacity={0.25} />
              <Line yAxisId="left" type="monotone" dataKey="price" name="Ціна, грн/МВт·год" stroke="#F97316" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
